import React, {
    useLayoutEffect,
    useState,
    useRef,
    useEffect
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { TransformWrapper, TransformComponent, useControls } from "react-zoom-pan-pinch";

import * as CONSTANTS from "../constants";
import * as QUERIES from "../query-functions";

import SeatButton from "./seat-button";
import CrossIcon from "../svg/cross"
import MinusIcon from "../svg/minus"
import LoadingIndicator from "../components/loading-indicator"

const Controls = () => {
    const { zoomIn, zoomOut } = useControls();
  
    return (
      <div className="seating-plan__controls">
        <button className='btn btn--square' onClick={() => zoomIn()}>
            <span className='sr-text'>Zoom in</span>
            <CrossIcon />
        </button>
        <button className='btn btn--square' onClick={() => zoomOut()}>
            <span className='sr-text'>Zoom out</span>
            <MinusIcon />
        </button>
      </div>
    );
};

const getImageDimensions = (url, containerRef) => {
    const horizontalPadding = 80;
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = url;
        img.onload = () => {
            const width = img.naturalWidth;
            const height = img.naturalHeight;
            let imageScale = 0;

            if (containerRef?.current) {
                const containerRect = containerRef.current.getBoundingClientRect();

                // Subtract horizontal padding from total width to account for controls on landscape images
                const adjustedWidth = containerRect.width - horizontalPadding * 2;

                imageScale = Math.min(
                    adjustedWidth / width,
                    containerRect.height / height
                );
            }

            resolve({ width, height, image_scale: imageScale });
        };
        img.onerror = reject;
    });
};

export default function SeatingPlan(props) {
    
    const [imageDimensions, setImageDimensions] = useState(null);
    const [selectedSeatIds, setSelectedSeatIds] = useState(null);
    const [planImage, setPlanImage] = useState(null);

    const containerRef = useRef(null);
    const zoomFactor = 8;
    const mobileInitScale = 0.5;
    
    // Allows image used to be overridden in the Event ACF
    const { data: customPlanImageData } = useQuery({
        queryKey: ['customPlanImageData', CONSTANTS.EVENT?.event_id],
        queryFn: QUERIES.getCustomPlanImageData,
        staleTime: 86400000,
        retry: false,
        refetchOnWindowFocus: false,
        enabled: CONSTANTS.HAS_CUSTOM_PLAN_IMAGES
    });
   
    const { isPending: seatStatusisPending, 
            isError: seatStatusisError,
            data: seatStatusData, 
            error: seatStatusError } = useQuery({
        queryKey: ['seatStatusData', CONSTANTS.INSTANCE_ID],
        queryFn: QUERIES.getSeatData
    });

    useEffect(() => {
        if (customPlanImageData) {
            setPlanImage(customPlanImageData.image)
        } else {
            setPlanImage(props.planData?.background_image_url);
        }
    }, [props.planData, customPlanImageData]);

    useLayoutEffect(() => {
        if (!planImage) return;
        // Wait until the ref is attached (retry if necessary)
        const checkContainerReady = () => {
            if (containerRef?.current) {
                (async () => {
                    const image_dimensions = await getImageDimensions(
                        planImage,
                        containerRef
                    );
                    setImageDimensions(image_dimensions);
                })();
            } else {
                // If container is not yet attached, wait and retry
                requestAnimationFrame(checkContainerReady);
            }
        };
        checkContainerReady();
    }, [planImage, containerRef.current]);

    

    // Determine initialScale based on device width
    const initialScale = (typeof window !== "undefined" && window.innerWidth <= 768)
        ? mobileInitScale
        : (imageDimensions ? imageDimensions.image_scale : 1);

    return (
        <div className="booking-pathway__main-content seating-plan" ref={containerRef} role="region" aria-live="polite"> 
            <h2 className="sr-text">Seating plan</h2>

            {seatStatusisError ? 
                <p className='error-message'>An error has occurred. {seatStatusError}</p>
            : null}
            
            { (props.planData && imageDimensions && !seatStatusisPending && !seatStatusisError && seatStatusData) ?
                (
                    <TransformWrapper      
                        initialScale={initialScale}
                        minScale={imageDimensions.image_scale}
                        maxScale={imageDimensions.image_scale * zoomFactor}
                        centerOnInit={true}>
                        {({ zoomIn, zoomOut, ...rest }) => (
                            <>
                            <Controls />
                            <TransformComponent
                                wrapperStyle={{
                                    width: "100%",
                                    height: "100%",
                                }}>
                                <div className={`seating-plan__plan seating-plan__plan--${props.planData.name?.toLowerCase().replaceAll(" ", "-")}`}>
                                        <img src={planImage} 
                                            alt="Seating Plan"
                                            style={{width: imageDimensions.width, height: imageDimensions.height}} />
                                    {props.planData.areas?.length ?
                                        props.planData.areas?.map((area) => (
                                            area.seats?.map((seat) => (
                                                <SeatButton key={seat.id} 
                                                            area={area}
                                                            seat={seat} 
                                                            statusData={seatStatusData} 
                                                            selectedArea={props.selectedArea}
                                                            selectedBands={props.selectedBands}
                                                            selectedSeatIds={selectedSeatIds}
                                                            setSelectedSeatIds={setSelectedSeatIds}
                                                            setSeatTicketIsPending={props.setSeatTicketIsPending}
                                                            setEnabledFilters={props.setEnabledFilters}
                                                            enabledFilters={props.enabledFilters} />
                                        ))
                                    ))
                                :
                                    props.planData.seats?.map((seat) => (
                                        <SeatButton key={seat.id}
                                                    seat={seat} 
                                                    statusData={seatStatusData} 
                                                    selectedArea={props.selectedArea}
                                                    selectedBands={props.selectedBands}
                                                    selectedSeatIds={selectedSeatIds}
                                                    setSelectedSeatIds={setSelectedSeatIds}
                                                    setSeatTicketIsPending={props.setSeatTicketIsPending}
                                                    setEnabledFilters={props.setEnabledFilters}
                                                    enabledFilters={props.enabledFilters} />
                                    ))
                                }
                                </div>
                            </TransformComponent>
                            </>
                        )}
                    </TransformWrapper>
                )
            : 
                (
                    <LoadingIndicator />
                )
            }
        </div>
    );
}